import { useState } from 'react';
import { useHistory } from 'react-router-dom';

import { PostLeaseSigningUpsell } from '@zg-rentals/renters-insurance';
import {
  Box,
  Button,
  Divider,
  Heading,
  Icon,
  Modal,
  Paragraph,
  Text,
  TextButton,
  Upsell,
} from '@zillow/constellation-10';
import { IconCheckmarkCircleFilled, IconMegaphoneFilled } from '@zillow/constellation-icons';
import { useRentalManagerUser } from '@zg-rentals/rental-manager-env';

import { Lease } from '../../../../../shared/models';
import { REGISTER_TO_VOTE_LINK } from '../../../../constants/stringConstants';
import { emitGoToMyLeasesEvent, emitRegisterToVoteEvent } from '../../../../../analytics/events';
import { trackEvent } from '../../../../../analytics';

type Props = {
  lease: Lease;
  isOpen: boolean;
  onClose(): void;
};

function TenantSigningCompleteModal({ isOpen, lease, onClose }: Props): JSX.Element | null {
  const { user } = useRentalManagerUser();
  const history = useHistory();
  const [isUpsellLoading, setIsUpsellLoading] = useState(true);
  const [shouldDisplayUpsell, setShouldDisplayUpsell] = useState(false);

  function onGoToLeases(): void {
    emitGoToMyLeasesEvent({ user, listing: null, lease });
    history.push(Lease.getDashboardPath());
  }

  if (!lease) {
    return null;
  }

  function onRegisterToVote() {
    emitRegisterToVoteEvent({ user, listing: null, lease });
    window.open(REGISTER_TO_VOTE_LINK, '_blank');
  }

  const onUpsellLoadingComplete = (shouldDisplayUpsell) => {
    setIsUpsellLoading(false);
    setShouldDisplayUpsell(shouldDisplayUpsell);
  };

  const nonUpsellFooter = (
    <Button tone="brand" emphasis="filled" disabled={isUpsellLoading} onClick={onGoToLeases}>
      Go to leases
    </Button>
  );

  const upsellFooter = (
    <PostLeaseSigningUpsell
      leaseId={`${lease.id}`}
      nonUpsellButton={nonUpsellFooter}
      onClose={onGoToLeases}
      onUpsellLoadingComplete={onUpsellLoadingComplete}
      trackClickstreamEvent={trackEvent}
    />
  );

  return (
    <Modal.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
      size={{ base: 'fullScreen', md: 'sm' }}
      useDismissProps={{
        escapeKey: false,
        outsidePress: false,
      }}
    >
      <Modal.Portal>
        <Modal.Backdrop>
          <Modal.Content>
            <Modal.Header>
              <Heading level={1}>Signing complete</Heading>
            </Modal.Header>
            <Divider />
            <Modal.Body>
              <>
                <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tight' }}>
                  <Icon size="lg" role="img" css={{ color: 'icon.feedback.success.strong' }}>
                    <IconCheckmarkCircleFilled />
                  </Icon>
                  <Heading level={2} css={{ textStyle: 'responsive.heading-xs' }}>
                    You’ve finished signing
                  </Heading>
                  <Paragraph>
                    You’re one step closer to moving in. Be sure to pay any remaining deposits outlined in your lease.
                    We’ll notify you once everyone signs so you can download your signed lease.
                  </Paragraph>
                  {!shouldDisplayUpsell && !isUpsellLoading && (
                    <>
                      <Heading level={3} css={{ textStyle: 'responsive.body-lg-bold', marginBottom: 'tighter' }}>
                        Next step
                      </Heading>
                      <Upsell
                        header={<Heading level={4}>Are you registered to vote for your new address?</Heading>}
                        body={<Text>Make your voice heard, register to vote today.</Text>}
                        media={
                          <Icon size="lg" role="img">
                            <IconMegaphoneFilled />
                          </Icon>
                        }
                        action={
                          <TextButton tone="brand" onClick={onRegisterToVote}>
                            Register to vote
                          </TextButton>
                        }
                        tone="soft"
                        elevated={false}
                        closeable={false}
                      />
                    </>
                  )}
                </Box>
              </>
            </Modal.Body>
            <Divider />
            <Modal.Footer>{upsellFooter}</Modal.Footer>
            {!shouldDisplayUpsell && !isUpsellLoading && <Modal.CloseButton />}
          </Modal.Content>
        </Modal.Backdrop>
      </Modal.Portal>
    </Modal.Root>
  );
}

export default TenantSigningCompleteModal;
